import { Card } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Quote, X, ExternalLink, Star } from "lucide-react";
import { BsLinkedin } from 'react-icons/bs';
import { Badge } from "@/components/ui/badge";  
import { Button } from "@/components/ui/button";
import { useState, useEffect } from "react";
import SudharsonKumar from "@/assets/profile/SudharsonKumar_sir.jpeg";
import Abhijeet_sir from "@/assets/profile/Abhijeet_sir.jpg";
import Puneet_Pandoh_sir from "@/assets/profile/puneet_sir.jpg";
const testimonials = [
  {
    name: "Puneet Pandoh",
    role: "Director of Software Engineering - Intel Tiber Edge Platform",
    company: "AI Prismatic Inc.",
    image: Puneet_Pandoh_sir,
    quote: `During his two-month internship at AI Prismatic Inc., Mohan developed a Cypress test suite that strengthened the reliability of our Kuizzo.com platform. He combined technical skill with professionalism, communicated progress clearly, and delivered results that continue to benefit our QA process.`,
    linkedin: "https://www.linkedin.com/in/puneetpandoh"
  },
  {
    name: "Abhijeet Kolekar",
    role: "Wireless and Security Leader | WiFi, 5G, 6G | System Security Architect| Leader| Zero Trust | PQC | 3GPP SA3 Delegate | Founder | Product manager| AI/ML| Cloud & SaaS | Driving 0---to---1 Innovation in Agentic AI",
    company: "Kuizzo.com",
    image: Abhijeet_sir,
    quote: `I'm happy to recommend Mohan Balaji, who worked with me on test automation at Kuizzo.com. He strengthened our suite across login, course creation, notes/flashcards, and reporting workflows; introduced useful assertions and test data strategies; and improved pipeline reliability. Mohan communicates clearly, asks smart questions, and follows through. Any team would be lucky to have him.`,
    linkedin: "https://www.linkedin.com/in/abhijeetkolekar"
  },
  {
    name: "Dr. Sudharson Kumar",
    role: "Professor || Passionate Researcher || Dedicated Educator || Exploring AI, Machine Learning, and Data Privacy || Quantumist",
    company: "RMD Engineering College",
    image: SudharsonKumar,
    quote: `It has been a real pleasure to guide and collaborate with Mohan Balaji from RMD Engineering College, whose passion, thinking skills, and leadership have astonished me. Mohan is both an excellent scholar and deeply interested in research and innovation, two features that distinguish him from the rest of his group.\n\nOur teamwork covers helpful projects such as filing a patent together for a solar-powered back cover for mobile phones, which was his way of bridging technology and daily use. In addition, we have focused on building many web applications and machine learning systems, and here Mohan's skills as an engineer were indispensable.`,
    linkedin: "https://www.linkedin.com/in/dr-sudharson-kumar/"
  }
]

const Testimonials = () => {
  const [selectedTestimonial, setSelectedTestimonial] = useState<typeof testimonials[0] | null>(null);

  const openModal = (testimonial: typeof testimonials[0]) => {
    setSelectedTestimonial(testimonial);
  };

  const closeModal = () => {
    setSelectedTestimonial(null);
  };

  // Keyboard support for modal
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (selectedTestimonial && event.key === 'Escape') {
        closeModal();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    if (selectedTestimonial) {
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [selectedTestimonial]);

  return (
    <section id="testimonials" className="py-20 bg-background">
      <div className="container mx-auto px-4">
        <Badge className="mb-4">Testimonials</Badge>
        <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-12 text-foreground text-center">Testimonials & Recommendations</h2>
        
        {/* Responsive Testimonials Container */}
        <div className="relative max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {testimonials.map((testimonial, index) => {
              return (
                <Card 
                  key={index}
                  className="group cursor-pointer bg-gradient-to-br from-background/80 to-muted/30 border border-border hover:border-primary/30 transition-all duration-300  hover:shadow-primary/10 hover:-translate-y-2 rounded-2xl overflow-hidden"
                  onClick={() => openModal(testimonial)}
                >
                  {/* Card Header */}
                  <div className="p-6 pb-4">
                    <div className="flex items-center gap-4 mb-4">
                      <Avatar className="h-14 w-14 border-2 border-primary/30 shadow-sm group-hover:scale-110 transition-transform duration-300">
                        <AvatarImage src={testimonial.image} alt={testimonial.name} />
                        <AvatarFallback className="text-sm font-semibold">
                          {testimonial.name.split(' ').map(n => n[0]).join('')}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-bold text-primary text-lg mb-1 truncate">
                          {testimonial.name}
                        </h3>
                        <p className="text-muted-foreground text-sm font-medium truncate">
                          {testimonial.company}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Quote Section */}
                  <div className="px-6 pb-4 flex-1">
                    <div className="relative">
                      <Quote className="absolute -top-2 -left-2 h-8 w-8 text-primary/20" />
                      <p className="text-muted-foreground text-sm leading-relaxed pl-6 line-clamp-4 italic">
                        {testimonial.quote}
                      </p>
                    </div>
                  </div>

                  {/* Card Footer */}
                  <div className="p-6 pt-4 border-t border-border bg-gradient-to-r from-primary/5 to-transparent">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <BsLinkedin className="h-4 w-4 text-[#0077B5]" />
                        <span className="text-xs text-muted-foreground">LinkedIn</span>
                      </div>
                      <div className="text-xs text-primary/70 font-medium">
                        Click to read more
                      </div>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      </div>

      {/* Interactive Modal Popup */}
      {selectedTestimonial && (
        <div 
          className="fixed inset-0 bg-black/60  z-50 flex items-center justify-center p-4 animate-in fade-in duration-300"
          onClick={closeModal}
        >
          <div 
            className="bg-background border border-border rounded-3xl shadow-2xl max-w-2xl sm:max-w-3xl w-full max-h-[90vh] overflow-y-auto animate-in zoom-in-95 slide-in-from-bottom-4 duration-300 mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header with Enhanced Design */}
            <div className="relative p-4 sm:p-6 border-b border-border bg-gradient-to-r from-primary/5 to-purple-500/5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 sm:gap-6">
                  <div className="relative">
                    <Avatar className="h-16 w-16 sm:h-20 sm:w-20 shadow-sm border-4 border-primary/30 ring-4 ring-primary/10">
                      <AvatarImage src={selectedTestimonial.image} alt={selectedTestimonial.name} />
                      <AvatarFallback className="text-lg font-bold">{selectedTestimonial.name.split(' ').map(n => n[0]).join('')}</AvatarFallback>
                    </Avatar>
                    <div className="absolute -bottom-2 -right-2 w-4 h-4 sm:w-6 sm:h-6 bg-green-500 rounded-full border-2 border-background animate-pulse"></div>
                  </div>
                  <div>
                    <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold text-primary mb-1">{selectedTestimonial.name}</h3>
                    <p className="text-muted-foreground font-medium text-sm sm:text-base lg:text-lg mb-1">{selectedTestimonial.role}</p>
                    <p className="text-xs sm:text-sm text-muted-foreground bg-white/5 px-2 sm:px-3 py-1 rounded-full inline-block">
                      {selectedTestimonial.company}
                    </p>
                  </div>
                </div>
                <Button
                  variant="ghostlinkedin"
                  size="icon"
                  onClick={closeModal}
                  className="text-muted-foreground hover:text-foreground hover:bg-white/10 rounded-full transition-all duration-200 hover:scale-110"
                >
                  <X className="h-6 w-6" />
                </Button>
              </div>
            </div>

            {/* Modal Content with Enhanced Layout */}
            <div className="p-4 sm:p-6 lg:p-8">
              <div className="relative mb-6 sm:mb-8">
                <Quote className="absolute -top-4 -left-4 sm:-top-6 sm:-left-6 h-16 w-16 sm:h-20 sm:w-20 text-primary/10 animate-pulse" />
                <div className="bg-gradient-to-r from-primary/5 to-purple-500/5 p-4 sm:p-6 rounded-2xl border border-border">
                  <p className="text-base sm:text-lg lg:text-xl italic text-muted-foreground whitespace-pre-line pl-6 sm:pl-8 leading-relaxed">
                    {selectedTestimonial.quote}
                  </p>
                </div>
              </div>

              {/* Enhanced Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 pt-6 border-t border-border">
                <Button
                  asChild
                  className="flex-1 bg-gradient-to-r from-primary to-purple-500 hover:from-primary/90 hover:to-purple-500/90 text-foreground shadow-sm hover:shadow-sm transition-all duration-300 hover:-translate-y-0.5"
                >
                  <a
                    href={selectedTestimonial.linkedin}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-3 py-3"
                  >
                    <BsLinkedin className="h-5 w-5" />
                    <span className="font-semibold">View on LinkedIn</span>
                    <ExternalLink className="h-4 w-4" />
                  </a>
                </Button>
                <Button
                  asChild
                  variant="outline"
                  className="flex-1 border-primary/30 hover:bg-primary/10 hover:border-primary/50 transition-all duration-300 hover:-translate-y-0.5"
                >
                  <a
                    href="https://www.linkedin.com/in/mohanbalaji2004/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center gap-3 py-3"
                  >
                    <BsLinkedin className="h-4 w-4" />
                    <span className="font-semibold">Connect with Mohan</span>
                    <ExternalLink className="h-3 w-3" />
                  </a>
                </Button>
              </div>

              {/* Additional Interactive Elements */}
              <div className="mt-6 flex items-center justify-center gap-4 text-sm text-muted-foreground">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                  <span>Verified Recommendation</span>
                </div>
                <div className="w-px h-4 bg-white/20"></div>
                <div className="flex items-center gap-2">
                  <Star className="h-4 w-4 text-yellow-500" />
                  <span>Professional Endorsement</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export { Testimonials };
export default Testimonials; 
